/**
 * Component 12: Utilities & Helpers - Index
 * 
 * Centralized export point for all utility modules
 */

// Import all utility modules
import DateUtils from './DateUtils';
import NumberUtils from './NumberUtils';
import ValidationUtils, { ValidationResult } from './ValidationUtils';
import BusinessLogic from './BusinessLogic';
import Constants from './Constants';
import Helpers from './Helpers';

/**
 * Main utilities object
 */
export const utils = {
  // Date and time utilities
  date: DateUtils,
  
  // Number and currency utilities
  number: NumberUtils,
  
  // Validation utilities
  validation: ValidationUtils,
  
  // Business logic utilities
  business: BusinessLogic,
  
  // Application constants
  constants: Constants,
  
  // Helper utilities
  helpers: Helpers
};

/**
 * Quick access to commonly used utilities
 */
export const quickUtils = {
  // Date formatting
  formatDate: DateUtils.formatDate,
  formatDateBG: DateUtils.formatDateBG,
  formatDateEN: DateUtils.formatDateEN,
  formatDateTime: DateUtils.formatDateTime,
  formatTime: DateUtils.formatTime,
  getRelativeTime: DateUtils.getRelativeTime,
  
  // Number formatting
  formatNumber: NumberUtils.formatNumber,
  formatPercentage: NumberUtils.formatPercentage,
  formatCurrency: NumberUtils.formatCurrency,
  formatBGN: NumberUtils.formatBGN,
  formatEUR: NumberUtils.formatEUR,
  formatOdds: NumberUtils.formatOdds,
  
  // Validation
  validateEmail: ValidationUtils.validateEmail,
  validatePassword: ValidationUtils.validatePassword,
  validatePhoneBG: ValidationUtils.validatePhoneBG,
  validateStake: ValidationUtils.validateStake,
  validateOdds: ValidationUtils.validateOdds,
  
  // Business logic
  calculateMargin: BusinessLogic.calculateMargin,
  calculateStakeDistribution: BusinessLogic.calculateStakeDistribution,
  calculateProfitScenarios: BusinessLogic.calculateProfitScenarios,
  calculateROI: BusinessLogic.calculateROI,
  
  // Helpers
  debounce: Helpers.debounce,
  throttle: Helpers.throttle,
  deepClone: Helpers.deepClone,
  deepEqual: Helpers.deepEqual,
  copyToClipboard: Helpers.copyToClipboard,
  
  // Constants access
  API_ENDPOINTS: Constants.API_ENDPOINTS,
  USER_ROLES: Constants.USER_ROLES,
  MARKET_TYPES: Constants.MARKET_TYPES,
  BOOKMAKERS: Constants.BOOKMAKERS
};

/**
 * Initialize utilities module
 * @param {Object} options - Initialization options
 * @returns {Object} Initialization result
 */
export function initializeUtils(options = {}) {
  try {
    return {
      success: true,
      modules: {
        date: 'DateUtils',
        number: 'NumberUtils',
        validation: 'ValidationUtils',
        business: 'BusinessLogic',
        constants: 'Constants',
        helpers: 'Helpers'
      },
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error initializing utils module:', error);
    return {
      success: false,
      error: error.message,
      timestamp: new Date().toISOString()
    };
  }
}

/**
 * Module info for utilities
 */
export const UTILS_MODULE_INFO = {
  name: 'SureBet Utilities & Helpers',
  version: '1.0.0',
  description: 'Comprehensive utility functions for date/time, number/currency formatting, validation, business logic, constants, and helpers',
  modules: {
    DateUtils: {
      description: 'Date and time formatting and manipulation utilities',
      features: [
        'Bulgarian and English date formatting',
        'Relative time formatting',
        'Timezone handling',
        'Date validation and parsing',
        'Duration calculation',
        'Calendar utilities'
      ]
    },
    NumberUtils: {
      description: 'Number and currency formatting utilities',
      features: [
        'Percentage formatting',
        'Currency formatting (BGN, EUR)',
        'Odds formatting (Decimal, Fractional, American)',
        'Number validation and parsing',
        'Mathematical utilities',
        'Number comparison and sorting'
      ]
    },
    ValidationUtils: {
      description: 'Comprehensive validation utilities',
      features: [
        'Email validation',
        'Password strength validation',
        'Bulgarian phone number validation',
        'Betting-specific validations (stakes, odds)',
        'Form field validation helpers',
        'Validation error message generation'
      ]
    },
    BusinessLogic: {
      description: 'Arbitrage calculations and betting logic utilities',
      features: [
        'Margin calculation utilities',
        'Stake distribution calculation',
        'Profit calculation functions',
        'Odds conversion utilities',
        'Risk assessment helpers',
        'Betting scenario analysis tools'
      ]
    },
    Constants: {
      description: 'Application-wide constants and configuration',
      features: [
        'API endpoints and timeout values',
        'User roles and permissions',
        'Market type definitions',
        'Error message constants',
        'Refresh interval configuration',
        'Validation rules and limits'
      ]
    },
    Helpers: {
      description: 'General-purpose utility functions',
      features: [
        'Debounce and throttle utilities',
        'Object deep comparison',
        'Array manipulation helpers',
        'String formatting utilities',
        'Browser detection and feature checking',
        'Performance measurement utilities'
      ]
    }
  }
};

/**
 * Export individual modules for direct import
 */
export {
  DateUtils,
  NumberUtils,
  ValidationUtils,
  ValidationResult,
  BusinessLogic,
  Constants,
  Helpers
};

/**
 * Re-export constants for convenience
 */
export const {
  API_ENDPOINTS,
  API_TIMEOUTS,
  HTTP_STATUS,
  USER_ROLES,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  SUBSCRIPTION_STATUS,
  MARKET_TYPES,
  COEFFICIENT_TYPES,
  BOOKMAKERS,
  SPORTS,
  LANGUAGES,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  REFRESH_INTERVALS,
  VALIDATION_RULES,
  STORAGE_KEYS,
  LIMITS,
  PAGINATION,
  DATE_TIME,
  UI_CONSTANTS,
  FEATURE_FLAGS,
  EVENTS,
  BETTING_LIMITS,
  ODDS_FORMAT,
  RISK_LEVELS
} = Constants;

/**
 * Export main utilities object as default
 */
export default utils;
